const OPENERS: Record<string, string> = { '(': ')', '[': ']', '{': '}' }
const CLOSERS = new Set([')', ']', '}'])

type Mode = 'code' | 'single' | 'double' | 'template' | 'line' | 'block'

const stringMode = (ch: string): Mode | null =>
  ch === "'" ? 'single' : ch === '"' ? 'double' : ch === '`' ? 'template' : null

const quoteFor = (mode: Mode): string => (mode === 'single' ? "'" : mode === 'double' ? '"' : '`')

/**
 * Closes whatever brackets are still open so a half-typed plot can be
 * previewed. Preview only: the document and the keystroke log never see
 * this. Returns the input unchanged when a closer doesn't match, since
 * that code is broken rather than unfinished.
 */
export const healBrackets = (doc: string): string => {
  const stack: string[] = []
  let mode: Mode = 'code'
  for (let i = 0; i < doc.length; i++) {
    const ch = doc[i] as string
    const next = doc[i + 1]
    if (mode === 'line') {
      if (ch === '\n') mode = 'code'
      continue
    }
    if (mode === 'block') {
      if (ch === '*' && next === '/') {
        mode = 'code'
        i += 1
      }
      continue
    }
    if (mode !== 'code') {
      if (ch === '\\') i += 1
      else if (ch === quoteFor(mode)) mode = 'code'
      else if (mode !== 'template' && ch === '\n') mode = 'code'
      continue
    }
    if (ch === '/' && next === '/') mode = 'line'
    else if (ch === '/' && next === '*') mode = 'block'
    else if (stringMode(ch)) mode = stringMode(ch) as Mode
    else if (OPENERS[ch]) stack.push(OPENERS[ch] as string)
    else if (CLOSERS.has(ch)) {
      if (stack.pop() !== ch) return doc
    }
  }
  if (mode === 'single' || mode === 'double' || mode === 'template') return doc
  if (stack.length === 0) return doc
  // Closers appended inside an unfinished comment would be swallowed by it.
  const exit = mode === 'line' ? '\n' : mode === 'block' ? ' */' : ''
  return doc + exit + [...stack].reverse().join('')
}
